import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { formatShiftTime } from '../../utils/operationsDashboardUtils';
import { getPriorityColor, OPS_COLORS } from '../../constants/operationsDashboardColors';
import { DashboardDensity, isCompact } from '../../utils/dashboardDensity';

interface ExpandedAlertsPanelProps {
  alerts: any[];
  rows: any[];
  canManage: boolean;
  findRowForAlert: (alert: any) => any;
  onAssignCoverage: (row: any) => void;
  onNotRequired: (row: any) => void;
  onViewEmployee: (row: any) => void;
  density?: DashboardDensity;
  embedded?: boolean;
}

function getRecommendedAction(alert: any): string {
  if (alert.alertType === 'uncovered') return 'Assign covering agent immediately.';
  if (alert.alertType === 'coverage_load') return 'Rebalance agents across campaigns.';
  if (alert.alertType === 'late') return 'Confirm agent clock-in.';
  return 'Review and take action.';
}

const ExpandedAlertsPanel: React.FC<ExpandedAlertsPanelProps> = ({
  alerts,
  canManage,
  findRowForAlert,
  onAssignCoverage,
  onNotRequired,
  onViewEmployee,
  density = 'standard',
  embedded = false,
}) => {
  const compact = isCompact(density);

  const content = (
    <>
      {!embedded && (
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Alerts ({alerts.length})
        </Typography>
      )}
      {alerts.length === 0 ? (
        <Typography variant="caption" color="text.secondary">No active alerts.</Typography>
      ) : (
        <Stack spacing={compact ? 0.75 : 1}>
          {alerts.map((alert, index) => {
            const row = findRowForAlert(alert);
            const priorityLabel = alert.priority === 'high' ? 'CRITICAL' : alert.priority === 'medium' ? 'WARNING' : 'INFO';

            return (
              <Box
                key={`${alert.priority}-${index}`}
                sx={{
                  p: compact ? 0.75 : 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderLeft: `3px solid ${getPriorityColor(alert.priority)}`,
                  borderRadius: 0.5,
                }}
              >
                <Chip
                  size="small"
                  label={priorityLabel}
                  sx={{
                    bgcolor: getPriorityColor(alert.priority),
                    color: '#fff',
                    height: 18,
                    fontSize: '0.6rem',
                    mb: 0.5,
                    '& .MuiChip-label': { px: 0.5 },
                  }}
                />
                {row?.campaignName && (
                  <Typography variant="caption" display="block" sx={{ fontSize: '0.7rem' }}>
                    <strong>{row.campaignName}</strong>
                    {row.agentName && ` · ${row.agentName}`}
                  </Typography>
                )}
                {row?.scheduledStartTime && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem' }}>
                    {formatShiftTime(row.scheduledStartTime, row.scheduledEndTime)}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem', mt: 0.25 }}>
                  {alert.message}
                </Typography>
                <Typography variant="caption" sx={{ color: OPS_COLORS.coverage, fontSize: '0.65rem', display: 'block' }}>
                  → {getRecommendedAction(alert)}
                </Typography>
                {alert.alertType === 'uncovered' && canManage && row && (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                    <Button size="small" variant="contained" sx={{ fontSize: '0.65rem', py: 0.25, px: 0.75 }} onClick={() => onAssignCoverage(row)}>
                      Assign
                    </Button>
                    <Button size="small" variant="outlined" sx={{ fontSize: '0.65rem', py: 0.25, px: 0.75 }} onClick={() => onNotRequired(row)}>
                      N/A
                    </Button>
                    <Button size="small" variant="text" sx={{ fontSize: '0.65rem', py: 0.25 }} onClick={() => onViewEmployee(row)}>
                      View
                    </Button>
                  </Stack>
                )}
              </Box>
            );
          })}
        </Stack>
      )}
    </>
  );

  if (embedded) return <Box>{content}</Box>;

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent sx={{ p: compact ? 1.5 : 2, '&:last-child': { pb: compact ? 1.5 : 2 } }}>
        {content}
      </CardContent>
    </Card>
  );
};

export default ExpandedAlertsPanel;
